import { useMemo, useState } from "react"
import { Box, Button, HStack, IconButton, Input, NativeSelect, SimpleGrid, Spacer, Stack, Table } from "@chakra-ui/react"
import { FaChevronDown, FaChevronLeft, FaChevronRight, FaChevronUp, FaPencilAlt, FaTrash, FaPlus, FaCheck, FaTimes } from "react-icons/fa"
import { useQueryClient } from "@tanstack/react-query"
import { Section, Text } from "@/components"
import { IrrDeal, IrrDealFund } from "@/types"
import { addIrrCashFlow, updateIrrCashFlow, deleteIrrCashFlow } from "@/api/irr"
import { toaster } from "@/components/ui/toaster"

const formatCurrency = (value: string | undefined) => {
  if (!value) return "—"
  const num = parseFloat(value)
  if (isNaN(num)) return value
  if (num < 0) return `($${Math.abs(num).toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })})`
  return `$${num.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`
}

const formatNumber = (value: number | string | undefined) => {
  if (value === undefined || value === null) return "—"
  const num = typeof value === "string" ? parseFloat(value) : value
  if (isNaN(num)) return String(value)
  return num.toLocaleString()
}

const formatDate = (dateString: string | undefined) => {
  if (!dateString) return "—"
  return new Date(dateString).toLocaleDateString()
}

const formatPercent = (value: string | undefined) => {
  if (!value) return "—"
  const num = parseFloat(value)
  if (isNaN(num)) return value
  return `${(num * 100).toFixed(1)}%`
}

type SortKey = keyof IrrDeal
type SortDirection = "asc" | "desc"

const getSortValue = (deal: IrrDeal, key: SortKey): number | string => {
  const val = deal[key]
  if (key === "companyName" || key === "lastName" || key === "dealManager") return String(val).toLowerCase()
  if (key === "inceptionDate" || key === "originalClosingDate" || key === "exitDate") return new Date(String(val)).getTime() || 0
  if (typeof val === "number") return val
  if (typeof val === "string") {
    const num = parseFloat(val)
    return isNaN(num) ? 0 : num
  }
  return 0
}

const DetailItem = ({ label, value }: { label: string; value: string }) => (
  <Stack gap={0}>
    <Text fontSize="xs" color="text.muted">{label}</Text>
    <Text fontSize="sm" fontWeight="medium">{value}</Text>
  </Stack>
)

// Blank form state for adding a new cash flow
const BLANK_FORM = { date: "", amount: "", memo: "" }

const DealCard = ({ deal }: { deal: IrrDeal }) => {
  const queryClient = useQueryClient()
  const [showCashFlows, setShowCashFlows] = useState(false)
  const [editingId, setEditingId] = useState<number | null>(null)
  const [editForm, setEditForm] = useState(BLANK_FORM)
  const [addForm, setAddForm] = useState(BLANK_FORM)
  const [showAddRow, setShowAddRow] = useState(false)
  const [saving, setSaving] = useState(false)

  const dealIdInt = parseInt(deal.dealId, 10)

  const invalidate = () => queryClient.invalidateQueries({ queryKey: ["irrDeals"] })

  const handleStartEdit = (cf: IrrDeal["cashFlows"][0]) => {
    setEditingId(cf.id)
    setEditForm({ date: cf.date, amount: String(cf.amount), memo: cf.memo })
  }

  const handleSaveEdit = async () => {
    if (!editingId) return
    setSaving(true)
    try {
      await updateIrrCashFlow(editingId, editForm.date, Number(editForm.amount), editForm.memo)
      toaster.create({ title: "Cash flow updated", type: "success" })
      setEditingId(null)
      await invalidate()
    } catch {
      toaster.create({ title: "Failed to update cash flow", type: "error" })
    } finally {
      setSaving(false)
    }
  }

  const handleDelete = async (id: number) => {
    setSaving(true)
    try {
      await deleteIrrCashFlow(id)
      toaster.create({ title: "Cash flow deleted", type: "success" })
      await invalidate()
    } catch {
      toaster.create({ title: "Failed to delete cash flow", type: "error" })
    } finally {
      setSaving(false)
    }
  }

  const handleAdd = async () => {
    if (!addForm.date || !addForm.amount) {
      toaster.create({ title: "Date and amount are required", type: "warning" })
      return
    }
    setSaving(true)
    try {
      await addIrrCashFlow(dealIdInt, addForm.date, Number(addForm.amount), addForm.memo)
      toaster.create({ title: "Cash flow added", type: "success" })
      setAddForm(BLANK_FORM)
      setShowAddRow(false)
      await invalidate()
    } catch {
      toaster.create({ title: "Failed to add cash flow", type: "error" })
    } finally {
      setSaving(false)
    }
  }

  return (
    <Stack
      border="1px solid"
      borderColor="border.default"
      borderRadius="md"
      p={5}
      bg="card.bg"
      gap={4}
    >
      {/* Header */}
      <HStack justify="space-between" align="flex-start">
        <Stack gap={0}>
          <Text fontSize="lg" fontWeight="bold">{deal.companyName}</Text>
          <Text fontSize="sm" color="text.muted">
            {deal.lastName} — Deal {deal.dealId}
          </Text>
        </Stack>
        <Stack gap={0} align="flex-end">
          <Text fontSize="sm" color="text.muted">{deal.dealManager}</Text>
          {deal.fullyExitedPortfolio === "Yes" ? (
            <Text fontSize="xs" color="red.400" fontWeight="semibold">Exited</Text>
          ) : (
            <Text fontSize="xs" color="green.400" fontWeight="semibold">Active</Text>
          )}
        </Stack>
      </HStack>

      {/* Details grid */}
      <SimpleGrid columns={{ base: 2, md: 4, lg: 6 }} gap={4}>
        <DetailItem label="Inception" value={formatDate(deal.inceptionDate)} />
        <DetailItem label="Closing" value={formatDate(deal.originalClosingDate)} />
        <DetailItem label="Shares" value={formatNumber(deal.numSharesCurrent)} />
        <DetailItem label="FMV Current" value={formatCurrency(deal.fmvCurrent)} />
        <DetailItem label="LRP" value={formatCurrency(deal.lrpPrice)} />
        <DetailItem label="PP" value={formatPercent(deal.actualDealPp)} />
        <DetailItem label="ESO Payment" value={formatCurrency(deal.esoTotalPayment)} />
        <DetailItem label="LP Payment" value={formatCurrency(deal.lpPayment)} />
        <DetailItem label="Residual" value={formatCurrency(deal.residualValue)} />
        <DetailItem label="Realized" value={formatCurrency(deal.totalRealizations)} />
        <DetailItem label="Total Value" value={formatCurrency(deal.totalValue)} />
        <DetailItem label="LPx" value={`${parseFloat(deal.lpMultiple || "0").toFixed(2)}x`} />
        <DetailItem label="Holding Value (FMV)" value={deal.holdingValue ? formatCurrency(deal.holdingValue) : "—"} />
        <DetailItem label="Residual @2×LRP" value={deal.residualAt2xLrp ? formatCurrency(deal.residualAt2xLrp) : "—"} />
        <DetailItem label="IRR" value={deal.irr !== null ? formatPercent(deal.irr) : "—"} />
        <DetailItem label="TVPI" value={deal.tvpi !== null ? `${parseFloat(deal.tvpi).toFixed(2)}x` : "—"} />
        <DetailItem label="DPI" value={deal.dpi !== null ? `${parseFloat(deal.dpi).toFixed(2)}x` : "—"} />
        {deal.exitDate && <DetailItem label="Exit Date" value={formatDate(deal.exitDate)} />}
        {deal.industry && <DetailItem label="Industry" value={deal.industry} />}
        {deal.geography && <DetailItem label="Geography" value={deal.geography} />}
      </SimpleGrid>

      {/* Cash Flows */}
      <Stack gap={2}>
        <HStack>
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setShowCashFlows((prev) => !prev)}
            gap={1}
          >
            Cash Flows ({deal.cashFlows.length})
            {showCashFlows ? <FaChevronUp size={10} /> : <FaChevronDown size={10} />}
          </Button>
          <IconButton
            aria-label="Add cash flow"
            size="xs"
            variant="ghost"
            onClick={() => { setShowCashFlows(true); setShowAddRow(true) }}
          >
            <FaPlus size={10} />
          </IconButton>
        </HStack>

        {showCashFlows && (
          <Box overflowX="auto">
            <Table.Root size="sm">
              <Table.Header>
                <Table.Row bg="table.row">
                  <Table.ColumnHeader>Date</Table.ColumnHeader>
                  <Table.ColumnHeader>Amount</Table.ColumnHeader>
                  <Table.ColumnHeader>Memo</Table.ColumnHeader>
                  <Table.ColumnHeader w="80px" />
                </Table.Row>
              </Table.Header>
              <Table.Body>
                {deal.cashFlows.map((cf) => (
                  <Table.Row key={cf.id} bg="table.row">
                    {editingId === cf.id ? (
                      <>
                        <Table.Cell>
                          <Input
                            size="xs"
                            type="date"
                            value={editForm.date}
                            onChange={(e) => setEditForm((f) => ({ ...f, date: e.target.value }))}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="xs"
                            type="number"
                            value={editForm.amount}
                            onChange={(e) => setEditForm((f) => ({ ...f, amount: e.target.value }))}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <Input
                            size="xs"
                            value={editForm.memo}
                            onChange={(e) => setEditForm((f) => ({ ...f, memo: e.target.value }))}
                          />
                        </Table.Cell>
                        <Table.Cell>
                          <HStack gap={1}>
                            <IconButton aria-label="Save" size="xs" variant="ghost" onClick={handleSaveEdit} loading={saving}>
                              <FaCheck size={10} />
                            </IconButton>
                            <IconButton aria-label="Cancel" size="xs" variant="ghost" onClick={() => setEditingId(null)}>
                              <FaTimes size={10} />
                            </IconButton>
                          </HStack>
                        </Table.Cell>
                      </>
                    ) : (
                      <>
                        <Table.Cell whiteSpace="nowrap">{formatDate(cf.date)}</Table.Cell>
                        <Table.Cell whiteSpace="nowrap">{formatCurrency(String(cf.amount))}</Table.Cell>
                        <Table.Cell>{cf.memo || "—"}</Table.Cell>
                        <Table.Cell>
                          <HStack gap={1}>
                            <IconButton aria-label="Edit" size="xs" variant="ghost" onClick={() => handleStartEdit(cf)}>
                              <FaPencilAlt size={10} />
                            </IconButton>
                            <IconButton aria-label="Delete" size="xs" variant="ghost" colorPalette="red" onClick={() => handleDelete(cf.id)} loading={saving}>
                              <FaTrash size={10} />
                            </IconButton>
                          </HStack>
                        </Table.Cell>
                      </>
                    )}
                  </Table.Row>
                ))}

                {/* Add row */}
                {showAddRow && (
                  <Table.Row bg="table.row">
                    <Table.Cell>
                      <Input
                        size="xs"
                        type="date"
                        value={addForm.date}
                        onChange={(e) => setAddForm((f) => ({ ...f, date: e.target.value }))}
                        placeholder="Date"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        size="xs"
                        type="number"
                        value={addForm.amount}
                        onChange={(e) => setAddForm((f) => ({ ...f, amount: e.target.value }))}
                        placeholder="Amount"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <Input
                        size="xs"
                        value={addForm.memo}
                        onChange={(e) => setAddForm((f) => ({ ...f, memo: e.target.value }))}
                        placeholder="Memo"
                      />
                    </Table.Cell>
                    <Table.Cell>
                      <HStack gap={1}>
                        <IconButton aria-label="Save" size="xs" variant="ghost" onClick={handleAdd} loading={saving}>
                          <FaCheck size={10} />
                        </IconButton>
                        <IconButton aria-label="Cancel" size="xs" variant="ghost" onClick={() => { setShowAddRow(false); setAddForm(BLANK_FORM) }}>
                          <FaTimes size={10} />
                        </IconButton>
                      </HStack>
                    </Table.Cell>
                  </Table.Row>
                )}
              </Table.Body>
            </Table.Root>
          </Box>
        )}
      </Stack>
    </Stack>
  )
}

interface Props {
  isLoading: boolean
  irrDealFunds: IrrDealFund[]
}

export const IrrDealsComponent = ({ irrDealFunds }: Props) => {
  const fundNames = useMemo(() => {
    return [...irrDealFunds]
      .sort((a, b) => {
        const numA = parseInt(a.whichFund.replace(/\D/g, ""), 10)
        const numB = parseInt(b.whichFund.replace(/\D/g, ""), 10)
        if (!isNaN(numA) && !isNaN(numB)) return numA - numB
        return a.whichFund.localeCompare(b.whichFund)
      })
      .map((f) => f.whichFund)
  }, [irrDealFunds])

  const [currentFund, setCurrentFund] = useState<string | null>(null)
  const [sortKey, setSortKey] = useState<SortKey | null>(null)
  const [sortDirection, setSortDirection] = useState<SortDirection>("asc")
  const [searchQuery, setSearchQuery] = useState("")
  const [hideZeroShares, setHideZeroShares] = useState(false)
  const [managerFilter, setManagerFilter] = useState("All")
  const [currentPage, setCurrentPage] = useState(1)
  const pageSize = 50

  const activeFundName = currentFund ?? fundNames[0] ?? null

  const managerNames = useMemo(() => {
    if (activeFundName === null) return []
    const fund = irrDealFunds.find((f) => f.whichFund === activeFundName)
    const names = Array.from(new Set((fund?.deals ?? []).map((d) => d.dealManager).filter(Boolean)))
    return names.sort()
  }, [irrDealFunds, activeFundName])

  const activeDeals = useMemo(() => {
    if (activeFundName === null) return []
    const fund = irrDealFunds.find((f) => f.whichFund === activeFundName)
    let deals = fund?.deals ?? []
    if (hideZeroShares) {
      deals = deals.filter((d) => Number(d.residualValue) > 0)
    }
    if (managerFilter !== "All") {
      deals = deals.filter((d) => d.dealManager === managerFilter)
    }
    if (searchQuery.trim()) {
      const query = searchQuery.trim().toLowerCase()
      deals = deals.filter(
        (d) =>
          d.companyName.toLowerCase().includes(query) ||
          d.lastName.toLowerCase().includes(query)
      )
    }
    if (sortKey) {
      deals = [...deals].sort((a, b) => {
        const aVal = getSortValue(a, sortKey)
        const bVal = getSortValue(b, sortKey)
        let result: number
        if (typeof aVal === "string" && typeof bVal === "string") {
          result = aVal.localeCompare(bVal)
        } else {
          result = (aVal as number) - (bVal as number)
        }
        return sortDirection === "asc" ? result : -result
      })
    }
    return deals
  }, [irrDealFunds, activeFundName, sortKey, sortDirection, searchQuery, hideZeroShares, managerFilter])

  const totalPages = Math.max(1, Math.ceil(activeDeals.length / pageSize))
  const paginatedDeals = useMemo(() => {
    const start = (currentPage - 1) * pageSize
    return activeDeals.slice(start, start + pageSize)
  }, [activeDeals, currentPage, pageSize])

  const handleFilterChange = () => setCurrentPage(1)

  const handleSort = (key: SortKey) => {
    handleFilterChange()
    if (sortKey === key) {
      setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))
    } else {
      setSortKey(key)
      setSortDirection("asc")
    }
  }

  if (irrDealFunds.length === 0) {
    return <Text color="text.muted">No deal data available.</Text>
  }

  return (
    <Section>
      {/* Fund selector */}
      <HStack gap={2} mb={6}>
        {fundNames.map((name) => (
          <Stack
            key={name}
            px={4}
            py={2}
            borderRadius="md"
            cursor="pointer"
            bg={name === activeFundName ? "blue.600" : "transparent"}
            color={name === activeFundName ? "white" : "text.secondary"}
            border="1px solid"
            borderColor={name === activeFundName ? "blue.600" : "border.default"}
            _hover={{ bg: name === activeFundName ? "blue.700" : "hover.bg" }}
            onClick={() => { setCurrentFund(name); handleFilterChange() }}
            gap={0}
            alignItems="center"
          >
            <Text fontWeight="semibold" fontSize="sm">
              {name}
            </Text>
            <Text fontSize="xs" opacity={0.8}>
              {irrDealFunds.find((f) => f.whichFund === name)?.deals.filter((d) => Number(d.residualValue) > 0).length ?? 0} Unexited Deals
            </Text>
          </Stack>
        ))}
      </HStack>

      <HStack mb={4} flexWrap="wrap" gap={2}>
        <Button
          size="sm"
          w="fit-content"
          variant={hideZeroShares ? "solid" : "outline"}
          onClick={() => { setHideZeroShares((prev) => !prev); handleFilterChange() }}
          _dark={{ borderColor: hideZeroShares ? undefined : "whiteAlpha.500" }}
        >
          {hideZeroShares ? "Show All Deals" : "Hide Exited Deals"}
        </Button>
        <NativeSelect.Root size="sm" w="180px">
          <NativeSelect.Field
            _dark={{ borderColor: "whiteAlpha.500" }}
            value={managerFilter}
            onChange={(e) => { setManagerFilter(e.target.value); handleFilterChange() }}
          >
            <option value="All">All Managers</option>
            {managerNames.map((name) => (
              <option key={name} value={name}>
                {name}
              </option>
            ))}
          </NativeSelect.Field>
        </NativeSelect.Root>
        <Input
          size="sm"
          placeholder="Search deals..."
          maxW="250px"
          value={searchQuery}
          onChange={(e) => { setSearchQuery(e.target.value); handleFilterChange() }}
          _dark={{ borderColor: "whiteAlpha.500" }}
        />
        <NativeSelect.Root size="sm" w="180px">
          <NativeSelect.Field
            _dark={{ borderColor: "whiteAlpha.500" }}
            value={sortKey ?? ""}
            onChange={(e) => {
              const val = e.target.value as SortKey
              if (val) handleSort(val)
            }}
          >
            <option value="">Sort by...</option>
            <option value="companyName">Company</option>
            <option value="lastName">Client</option>
            <option value="dealManager">Manager</option>
            <option value="originalClosingDate">Closing Date</option>
            <option value="numSharesCurrent">Shares</option>
            <option value="esoTotalPayment">ESO Payment</option>
            <option value="residualValue">Residual</option>
            <option value="totalValue">Total Value</option>
            <option value="lpMultiple">LP Multiple</option>
          </NativeSelect.Field>
        </NativeSelect.Root>
        {sortKey && (
          <Button
            size="sm"
            variant="ghost"
            onClick={() => setSortDirection((prev) => (prev === "asc" ? "desc" : "asc"))}
          >
            {sortDirection === "asc" ? "Asc" : "Desc"}
          </Button>
        )}
      </HStack>

      {/* Header + pagination */}
      <HStack w="100%" justify="space-between" mb={4}>
        <Text fontSize="lg" fontWeight="bold">
          {activeFundName} Deals ({activeDeals.length})
        </Text>
        {activeDeals.length > pageSize && (
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              <FaChevronLeft size={10} />
            </Button>
            <Text fontSize="sm" color="text.secondary" whiteSpace="nowrap">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              <FaChevronRight size={10} />
            </Button>
          </HStack>
        )}
      </HStack>

      {/* Deal cards */}
      {activeDeals.length === 0 ? (
        <Text color="text.muted">No deals in this fund.</Text>
      ) : (
        <Stack gap={4}>
          {paginatedDeals.map((deal) => (
            <DealCard key={deal.dealId} deal={deal} />
          ))}
        </Stack>
      )}

      {/* Bottom pagination */}
      <HStack w="100%" mt={5}>
        <Spacer />
        {activeDeals.length > pageSize && (
          <HStack gap={2}>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
              disabled={currentPage === 1}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              <FaChevronLeft size={10} />
            </Button>
            <Text fontSize="sm" color="text.secondary" whiteSpace="nowrap">
              Page {currentPage} of {totalPages}
            </Text>
            <Button
              size="sm"
              variant="outline"
              onClick={() => setCurrentPage((p) => Math.min(totalPages, p + 1))}
              disabled={currentPage === totalPages}
              _dark={{ borderColor: "whiteAlpha.500" }}
            >
              <FaChevronRight size={10} />
            </Button>
          </HStack>
        )}
        <Spacer />
      </HStack>
    </Section>
  )
}
